"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FaArrowUp } from "react-icons/fa6";

const socialLinks = {
  linkedin: "https://www.linkedin.com/company/mileseducation/",
  youtube: "https://www.youtube.com/@MilesEducation",
  instagram: "https://www.instagram.com/miles.accounting/",
  twitter: "https://x.com/mileseducation",
};

const scrollTop = () =>
  typeof window !== "undefined" &&
  window.scrollTo({ top: 0, behavior: "smooth" });

const programs = [
  { name: "CPA", path: "/cpa" },
  { name: "CMA", path: "/cma" },
  { name: "EA (Enrolled Agent)", path: "/ea" },
  { name: "CAIRA", path: "/caira" },
  { name: "US Pathway", path: "/us-jobs-for-accountants" },
];

const CPACityURLs = [
  { name: "Bangalore", path: "/cpa/best-cpa-course-in-bangalore" },
  { name: "Chennai", path: "/cpa/best-cpa-course-in-chennai" },
  { name: "Delhi", path: "/cpa/cpa-coaching-in-delhi" },
  { name: "Ahmedabad", path: "/cpa/best-cpa-course-in-ahmedabad" },
  {
    name: "Hyderabad",
    path: "/cpa/certified-public-accountant-course-in-hyderabad",
  },
  { name: "Mumbai", path: "/cpa/cpa-course-fees-in-mumbai" },
  { name: "Pune", path: "/cpa/cpa-course-fees-in-pune" },
  { name: "Kochi", path: "/cpa/cpa-course-in-kerala" },
  { name: "Kolkata", path: "/cpa/cpa-coaching-in-kolkata" },
];

const CMACityURLs = [
  { name: "Bangalore", path: "/cma/best-cma-course-in-bangalore" },
  { name: "Chennai", path: "/cma/best-cma-institute-in-chennai" },
  { name: "Delhi", path: "/cma/best-cma-coaching-in-delhi" },
  { name: "Ahmedabad", path: "/cma/best-cma-course-in-ahmedabad" },
  { name: "Hyderabad", path: "/cma/best-cma-coaching-in-hyderabad" },
  { name: "Mumbai", path: "/cma/best-cma-classes-in-mumbai" },
  { name: "Pune", path: "/cma/best-cma-classes-in-pune" },
  { name: "Kochi", path: "/cma/best-cma-usa-institute-in-kerala" },
  { name: "Kolkata", path: "/cma/cma-kolkata" },
];

const legalLinks = [
  { name: "Contact Us", path: "/contact-us" },
  {
    name: "Careers",
    path: "https://mileseducation.zohorecruit.com/jobs/Careers",
  },
  { name: "Privacy Policy", path: "/privacy" },
  { name: "Terms of Service", path: "/terms-and-conditions" },
  { name: "About Us", path: "/about-us" },
  { name: "Blog", path: "/blog" },
];

const CMA_ASSETS =
  "https://ik.imagekit.io/mileseducation/miles_website/seo-pages/cma-course";

/* Brand marks are 48x48 PNGs rendered at 22px, so next/image re-encodes them
   to WebP/AVIF at 2x — no CDN transform needed on top. Declared once and
   mapped in both layouts so the two blocks can never drift apart. */
const socialIcons = [
  {
    href: socialLinks.linkedin,
    alt: "LinkedIn",
    src: `${CMA_ASSETS}/linkedin_icon.png`,
  },
  {
    href: socialLinks.youtube,
    alt: "YouTube",
    src: `${CMA_ASSETS}/youtube_icon.png`,
  },
  {
    href: socialLinks.instagram,
    alt: "Instagram",
    src: `${CMA_ASSETS}/instagram_icon.png`,
  },
  {
    href: socialLinks.twitter,
    alt: "Twitter",
    src: `${CMA_ASSETS}/twitter_icon.png`,
  },
];

const LocationLink = ({ loc }: { loc: { name: string; path: string } }) => (
  <Link href={loc.path} className="text-black text-xs md:text-sm">
    {loc.name}
  </Link>
);

const Separator = () => <span className="text-[#D5D5D5]">|</span>;

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-black text-base font-bold">{children}</h3>
);

const SocialIcon = ({
  href,
  alt,
  src,
}: {
  href: string;
  alt: string;
  src: string;
}) => (
  <Link
    href={href}
    aria-label={alt}
    target="_blank"
    className="bg-[#F5F5F7] rounded-full p-2.5 inline-flex transition-colors"
  >
    <Image src={src} alt={alt} width={22} height={22} loading="lazy" />
  </Link>
);

const StoreButton = ({
  href,
  icon,
  alt,
  name,
}: {
  href: string;
  icon: string;
  alt: string;
  name: string;
}) => (
  <Link
    href={href}
    target="_blank"
    className="bg-[#F5F5F7] transition-colors py-2 pl-2 pr-4 md:pr-6 flex items-center rounded-[8px] gap-2"
  >
    <Image
      src={icon}
      alt={alt}
      width={48}
      height={49}
      loading="lazy"
      className="shrink-0 w-7 h-7"
    />
    <div>
      <p className="text-[10px] text-left leading-3 text-black font-medium">
        Download on
      </p>
      <p className="text-sm md:text-[16px] text-black font-semibold leading-tight">
        {name}
      </p>
    </div>
  </Link>
);

const DownloadApp = ({ showQR = false }: { showQR?: boolean }) => (
  <div className="flex gap-4 justify-center md:justify-start">
    <div className="flex flex-row md:flex-col gap-3">
      <StoreButton
        href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
        icon="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp?tr=w-48,h-49,q-80"
        alt="App Store"
        name="App Store"
      />
      <StoreButton
        href="https://play.google.com/store/apps/details?id=com.miles.one"
        icon="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-48,h-49,q-80"
        alt="Play Store"
        name="Play Store"
      />
    </div>
    {showQR && (
      <div className="text-center hidden md:block">
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
          alt="QR Code"
          width={92}
          height={92}
          loading="lazy"
          className="min-w-[92px] min-h-[92px] inline-block"
        />
        <p className="pt-1 text-[10px] text-black">Scan to download</p>
      </div>
    )}
  </div>
);

export default function GlobalFooter() {
  const pathname = usePathname();
  const isCMAPage = pathname?.includes("/cma");
  const locations = useMemo(
    () => (isCMAPage ? CMACityURLs : CPACityURLs),
    [isCMAPage],
  );

  return (
    <footer className="bg-white relative max-md:border-t max-md:border-[#39F]">
      <div className="main_container relative z-10">
        <div className="bg-white md:rounded-[24px] md:shadow-[0_2px_30px_rgba(16,42,77,0.08)] md:px-[70px] md:pt-10 md:pb-7 max-md:pt-5 max-md:pb-4">
          {/* Scroll to Top */}
          <div className="flex justify-center items-center relative z-10">
            <button
              type="button"
              aria-label="Scroll to top"
              onClick={scrollTop}
              className="inline-flex items-center gap-2 bg-[#2A85FF] hover:bg-[#1F76EA] transition-colors text-white md:text-base text-[15px] font-bold px-5 py-2 rounded-2xl rounded-bl cursor-pointer"
            >
              <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-white/80 shrink-0">
                <FaArrowUp className="w-2.5 h-2.5" />
              </span>
              Scroll to Top
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden px-2 relative z-10">
            {/* Programs */}
            <div className="text-center mt-10">
              <SectionHeading>Accounting Programs</SectionHeading>
              <div className="flex justify-center items-center gap-2 flex-wrap mt-4">
                {programs?.map((prog, index) => (
                  <React.Fragment key={prog.name}>
                    <Link
                      href={prog.path}
                      className="text-[#333] text-xs md:text-sm"
                    >
                      {prog.name}
                    </Link>
                    {index < programs.length - 1 && <Separator />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="text-center mt-[30px]">
              <SectionHeading>Location</SectionHeading>
              <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
                {locations?.map((loc, index) => (
                  <React.Fragment key={loc.name}>
                    <LocationLink loc={loc} />
                    {index < locations.length - 1 && <Separator />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Connect */}
            <div className="text-center  mt-[30px]">
              <SectionHeading>Connect with us</SectionHeading>
              <div className="flex gap-4 justify-center mt-4">
                {socialIcons?.map((social) => (
                  <SocialIcon key={social.alt} {...social} />
                ))}
              </div>
            </div>

            {/* App Download */}
            <div className="text-center  mt-[30px]">
              <SectionHeading>Get Miles One App</SectionHeading>
              <p className="text-[#8C8C8C] text-xs md:text-sm mt-1 mb-4">
                The #1 App for AI-Ready Accountants
              </p>
              <DownloadApp />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block pt-12 relative z-10">
            <div className="grid grid-cols-4 gap-8">
              {/* Programs Column */}
              <div>
                <SectionHeading>Accounting Programs</SectionHeading>
                <div className="flex flex-col gap-3 mt-5">
                  {programs?.map((prog) => (
                    <Link
                      key={prog.name}
                      href={prog.path}
                      className="text-[#333] text-sm"
                    >
                      {prog.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Location Column */}
              <div>
                <SectionHeading>Location</SectionHeading>
                <div className="flex flex-col gap-3 mt-5">
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[0]} />
                    <Separator />
                    <LocationLink loc={locations[1]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[2]} />
                    <Separator />
                    <LocationLink loc={locations[3]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[4]} />
                    <Separator />
                    <LocationLink loc={locations[5]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[6]} />
                    <Separator />
                    <LocationLink loc={locations[7]} />
                    <Separator />
                    <LocationLink loc={locations[8]} />
                  </div>
                </div>
              </div>

              {/* Connect Column */}
              <div>
                <SectionHeading>Connect with us</SectionHeading>
                <div className="grid grid-cols-2 gap-4 w-fit mt-5">
                  {socialIcons?.map((social) => (
                    <SocialIcon key={social.alt} {...social} />
                  ))}
                </div>
              </div>

              {/* App Column */}
              <div>
                <SectionHeading>Get Miles One App</SectionHeading>
                <p className="text-[#00000099] text-[12px] mt-1 mb-4">
                  The #1 App for AI-Ready Accountants
                </p>
                <DownloadApp showQR />
              </div>
            </div>
          </div>

          {/* Bottom Bar — copyright leads on desktop, links lead on mobile */}
          <div className="relative z-10 md:mt-10 md:pt-5 md:border-t md:border-[#39F] max-md:mt-8 flex flex-col md:flex-row md:justify-between md:items-center md:gap-3">
            <p className="order-2 md:order-1 max-md:mt-3.5 text-[#00000099] text-[11px] md:text-sm text-center md:text-left">
              © 2026 Miles Education Private Limited. All rights reserved.
            </p>
            <div className="order-1 md:order-2 flex flex-wrap justify-center items-center gap-x-3 gap-y-1 md:gap-x-5">
              {legalLinks?.map((link, index) => (
                <React.Fragment key={link.name}>
                  <Link
                    href={link.path}
                    className="text-black md:text-[#165496] text-xs font-semibold"
                  >
                    {link.name}
                  </Link>
                  {/* The third separator would dangle at the end of the
                      first mobile row, so it is skipped there. */}
                  {index < legalLinks.length - 1 && index !== 2 && (
                    <span className="md:hidden text-[#0000004D]">|</span>
                  )}
                  {/* A full-width flex item forces the wrap, pinning the split
                      at 3:3 instead of leaving it to the text widths. */}
                  {index === 2 && (
                    <span className="w-full md:hidden" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Oversized Miles wordmark. Mobile matches GlobalFooterNewUi — 86% wide,
          24px above, uncropped; desktop keeps the cropped band. <picture> keeps
          it to one request per breakpoint (a display:none <img> is still
          downloaded) and the art stays palette PNG because WebP came out ~60%
          heavier on this flat two-colour graphic. */}
      <div className="relative z-0 flex justify-center overflow-hidden md:items-start md:h-[185px] md:mt-5">
        <picture className="block w-[86%] md:w-[810px] mt-6 md:mt-0">
          <source
            media="(min-width: 768px)"
            srcSet={`${CMA_ASSETS}/footer_logo_desktop.png?tr=w-810,f-png`}
          />
          {/* eslint-disable-next-line @next/next/no-img-element -- ImageKit URLs
              already carry their transforms, so no loader is needed. */}
          <img
            src={`${CMA_ASSETS}/footer_logo_mobile.png?tr=w-780,f-png`}
            alt=""
            aria-hidden="true"
            width={780}
            height={173}
            loading="lazy"
            decoding="async"
            className="w-full h-auto select-none pointer-events-none"
          />
        </picture>
      </div>
    </footer>
  );
}
